<template>
    <label v-if="showLabel" :for="inputName" :class="[
        labelClass,
        selection.required ? 'required-label' : '',
        optional ? 'optional-label' : '',
    ]">
        {{ label }}
    </label>
    <slot name="label-suffix"></slot>
    <select v-model="selected.value" class="form-select" :tabindex="tabindex" :class="[inputClass, {'is-invalid': isInvalid(meta, selection)}]" :name="inputName" :disabled="disabled">
        <option v-if="placeholderOption" :disabled="selection.required" :value="null">{{ placeholder }}</option>
        <option v-for="(option, index) in props.options" :key="index" :value="option.value">{{ option.label }}</option>
    </select>
    <span :class="errorClass">{{ getErrorMessage(selection, errorMessage) }}</span>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useField } from 'vee-validate'
import * as yup from 'yup'
import { copyValues, onReady, isInvalid, getErrorMessage, defaultInputName } from '@components/Utils'
import { Ref } from "vue";

const props = defineProps({
    selection: {
        required: true,
        type: Object,
    },
    options: {
        required: true,
        type: Object,
    },
    label: {
        type: String,
        default: null,
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    inputName: {
        type: String,
        default: defaultInputName(),
    },
    placeholder: {
        type: String,
        default: '',
    },
    inputClass: {
        type: String,
        default: 'form-select',
    },
    labelClass: {
        type: String,
        default: 'form-label'
    },
    errorClass: {
        type: String,
        default: 'text-danger form-text'
    },
    validation: {
        type: Object,
        default: yup.string().nullable(),
    },
    placeholderOption: {
        type: Boolean,
        default: true,
    },
    tabindex: {
        type: Number,
        default: 0
    },
    disabled: {
        type: Boolean,
        default: false
    },
    optional: {
        type: Boolean,
        default: false
    }
})

const mounted: Ref<boolean> = ref<boolean>(false)
const selected: Ref = ref(props.selection);

onReady(() => mounted.value = true)

const emit = defineEmits(['update:selection'])

const { errorMessage, setValue, validate, meta } = useField(<string>props.inputName, props.validation)

watch(
    () => selected.value.value,
    (value) => {
        if (mounted) {
            setValue(value)
            validate().then(() => {
                let returnValue = copyValues(selected.value)
                returnValue.value = meta.valid ? value : ''
                returnValue.valid = meta.valid
                emit('update:selection', returnValue)
            })
        }
    },
    { deep: true }
)
</script>
